import debounce from 'lodash/debounce';
import { action, extendObservable } from 'mobx';
import PropTypes from 'prop-types';
import AsyncActionStore from './AsyncActionStore';
import FuseSelectStore from './FuseSelectStore';

/**
 * Extension over FuseSelectStore
 * Allows searching an API for results to show in the options
 *
 * @prop {function} getSearchParams - the formatted filter value to send to the API
 * @prop {function} apiMethod - a function doing the actual API hit
 * @prop {function} searchValidator - Allows selective triggering of the search. If false removes previous search values
 * @prop {function} itemToValue - Used to convert API results into filter values. ID of the entity by default
 * @prop {number} debounceMs - Ms to wait after last user input before making an API call
 */
export default class FuseSearchableSelectStore extends FuseSelectStore {
    static PropType = PropTypes.shape({
        apiMethod: PropTypes.func.isRequired,
        getSearchParams: PropTypes.func.isRequired,
        itemToValue: PropTypes.func,
        searchStatus: AsyncActionStore.PropType,
        searchValidator: PropTypes.func,
        searchHandle: PropTypes.func,
        debounceMs: PropTypes.number,
        onSuccess: PropTypes.func,
        ...FuseSelectStore._PropType,
    });

    constructor(data = {}) {
        super();

        extendObservable(this, /** @class FuseSearchableSelectStore */{
            searchStatus: new AsyncActionStore(),
            debounceMs: 500,
        }, data);

        this.debouncedSearch = debounce(this.callApiMethodWithStatus, this.debounceMs);
    }

    /** What to do with the API's results. Converts them to values and sets them on the select by default */
    onSuccess = (items) =>
        this.setFilterValues(items.map(this.itemToValue || ((item) => item.id)));

    callApiMethodWithStatus = () => {
        this.searchStatus.setBase();
        this.searchStatus.setLoading(
            this.apiMethod(this.getSearchParams(this.getSearchFilter()))
                .then(this.onSuccess)
        );
    }

    /** Trimmed search value by default, but in case extra params are needed - can be overwritten */
    getSearchFilter = () =>
        this.filter.getValue()?.trim() ?? '';

    /** The handle is what is connected to the onFilter's event of a FuseSelect component */
    searchHandle = action((event) => {
        this.filter.onChangeHandler(event);

        if (!this.searchValidator?.(this.getSearchFilter())) {
            this.setFilterValues(null);
            return;
        }
        this.debouncedSearch();
    });
}
